import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { fieldLabel, t, translateEnum } from './copilotI18n';

const OMITTED_SCHEMA_FIELDS = ['message', 'context', 'confirmation'];

const valueForSchema = (value, schema) => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return undefined;
  if (schema?.type === 'array') {
    return String(value)
      .split(/[,\n]/)
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => valueForSchema(item, schema.items || { type: 'string' }));
  }
  if (schema?.type === 'integer' || schema?.type === 'number') return Number(value);
  if (schema?.type === 'boolean') return value === true || value === 'true';
  return value;
};

const schemaValueIsValid = (value, schema = {}) => {
  if (Array.isArray(schema.enum)) {
    const matchesEnum = schema.enum.some(option => (
      valueForSchema(option, schema) === value
    ));
    if (!matchesEnum) return false;
  }

  if (schema.type === 'array') {
    if (!Array.isArray(value)) return false;
    if (schema.minItems != null && value.length < Number(schema.minItems)) return false;
    if (schema.maxItems != null && value.length > Number(schema.maxItems)) return false;
    return value.every(item => schemaValueIsValid(item, schema.items || { type: 'string' }));
  }
  if (schema.type === 'integer') {
    if (!Number.isInteger(value)) return false;
  } else if (schema.type === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  } else if (schema.type === 'boolean') {
    return typeof value === 'boolean';
  } else if (schema.type === 'string') {
    if (typeof value !== 'string') return false;
    if (schema.minLength != null && value.length < Number(schema.minLength)) return false;
    if (schema.maxLength != null && value.length > Number(schema.maxLength)) return false;
    if (schema.pattern) {
      try {
        if (!(new RegExp(schema.pattern)).test(value)) return false;
      } catch (_error) {
        return false;
      }
    }
  }

  if (typeof value === 'number') {
    if (schema.minimum != null && value < Number(schema.minimum)) return false;
    if (schema.maximum != null && value > Number(schema.maximum)) return false;
  }
  return true;
};

const validationForSchemaField = (rawValue, schema = {}, required = false) => {
  const value = valueForSchema(rawValue, schema);
  if (value === undefined) {
    return {
      valid: !required,
      present: false,
      code: required ? 'required' : null
    };
  }

  if (schema.type === 'array' && Array.isArray(value)) {
    if (schema.minItems != null && value.length < Number(schema.minItems)) {
      return { valid: false, present: true, code: 'min_items', count: Number(schema.minItems) };
    }
    if (schema.maxItems != null && value.length > Number(schema.maxItems)) {
      return { valid: false, present: true, code: 'max_items', count: Number(schema.maxItems) };
    }
    if (!value.every(item => schemaValueIsValid(item, schema.items || { type: 'string' }))) {
      return { valid: false, present: true, code: 'invalid_list_item' };
    }
  } else if (!schemaValueIsValid(value, schema)) {
    return { valid: false, present: true, code: 'invalid' };
  }

  return { valid: true, present: true, code: null };
};

const hasSchemaValue = (context, field, schema) => {
  if (!Object.prototype.hasOwnProperty.call(context, field)) return false;
  return validationForSchemaField(context[field], schema, true).valid;
};

const initialContextFor = (properties, requiredFields, initialOrderExceptionCaseId) => (
  Object.entries(properties).reduce((context, [field, schema]) => {
    if (Object.prototype.hasOwnProperty.call(schema || {}, 'default')) {
      context[field] = schema.default;
    } else if (schema?.type === 'boolean' && requiredFields.includes(field)) {
      context[field] = false;
    }
    return context;
  }, initialOrderExceptionCaseId && properties.order_exception_case_id
    ? { order_exception_case_id: initialOrderExceptionCaseId }
    : {})
);

const AiChatMessenger = ({
  onCreateProposal,
  onChangeAction = () => {},
  selectedActionKey = null,
  selectedAction = null,
  initialOrderExceptionCaseId = null,
  isLoading = false,
  isEligible = true,
  isOnline = true
}) => {
  const [instruction, setInstruction] = useState('');
  const [actionContext, setActionContext] = useState({});
  const [touchedFields, setTouchedFields] = useState([]);
  const headingRef = useRef(null);
  const schemaProperties = useMemo(
    () => selectedAction?.input_schema?.properties || {},
    [selectedAction]
  );
  const actionFields = useMemo(() => Object.keys(schemaProperties)
    .filter(field => !OMITTED_SCHEMA_FIELDS.includes(field)), [schemaProperties]);
  const requiredActionFields = useMemo(() => (selectedAction?.input_schema?.required || [])
    .filter(field => !OMITTED_SCHEMA_FIELDS.includes(field)), [selectedAction]);
  const optionalActionFields = useMemo(() => actionFields
    .filter(field => !requiredActionFields.includes(field)), [actionFields, requiredActionFields]);
  const fieldValidations = useMemo(() => actionFields.reduce((validations, field) => ({
    ...validations,
    [field]: validationForSchemaField(
      actionContext[field],
      schemaProperties[field],
      requiredActionFields.includes(field)
    )
  }), {}), [actionContext, actionFields, requiredActionFields, schemaProperties]);
  const hasInvalidActionField = Object.values(fieldValidations).some(validation => !validation.valid);

  useEffect(() => {
    setActionContext(initialContextFor(
      schemaProperties,
      requiredActionFields,
      initialOrderExceptionCaseId
    ));
    setInstruction('');
    setTouchedFields([]);
    const focusTimer = window.setTimeout(() => headingRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [initialOrderExceptionCaseId, requiredActionFields, schemaProperties, selectedActionKey]);

  const normalizedActionContext = () => actionFields.reduce((context, field) => {
    const value = valueForSchema(actionContext[field], schemaProperties[field]);
    return value === undefined ? context : { ...context, [field]: value };
  }, {});

  const updateField = (field, value) => {
    setActionContext(previous => ({
      ...previous,
      [field]: value
    }));
    setTouchedFields(previous => previous.includes(field) ? previous : [...previous, field]);
  };

  const validationMessage = (validation) => {
    if (validation?.code === 'required') return t('field_required', 'Preencha este campo.');
    if (validation?.code === 'min_items') {
      return t('list_min_items', 'Informe pelo menos %{count} itens.', { count: validation.count });
    }
    if (validation?.code === 'max_items') {
      return t('list_max_items', 'Informe no máximo %{count} itens.', { count: validation.count });
    }
    if (validation?.code === 'invalid_list_item') {
      return t('list_invalid_item', 'Revise os itens: existe um valor inválido.');
    }
    return t('field_invalid', 'Revise o valor informado.');
  };

  const renderField = (field) => {
    const property = schemaProperties[field] || {};
    const isRequired = requiredActionFields.includes(field);
    const validation = fieldValidations[field];
    const showValidation = touchedFields.includes(field) && validation && !validation.valid;
    const descriptionId = property.description ? `ai-copilot-field-${field}-help` : null;
    const errorId = showValidation ? `ai-copilot-field-${field}-error` : null;
    const label = <span>{fieldLabel(field, property.title)}{isRequired ? ' *' : ''}</span>;
    const commonProps = {
      disabled: isLoading || !isEligible || !isOnline,
      'aria-describedby': [descriptionId, errorId].filter(Boolean).join(' ') || undefined,
      'aria-invalid': showValidation || undefined
    };
    let control;

    if (Array.isArray(property.enum)) {
      control = (
        <select
          {...commonProps}
          value={actionContext[field] ?? ''}
          onChange={event => updateField(field, event.target.value)}
          required={isRequired}
        >
          <option value="">{t('select_placeholder', 'Selecione uma opção')}</option>
          {property.enum.map(option => (
            <option key={String(option)} value={String(option)}>{String(option)}</option>
          ))}
        </select>
      );
    } else if (property.type === 'boolean') {
      control = (
        <span className="ai-copilot-checkbox-control">
          <input
            {...commonProps}
            type="checkbox"
            checked={actionContext[field] === true}
            onChange={event => updateField(field, event.target.checked)}
          />
          <span>{t('boolean_help', 'Ativar esta opção')}</span>
        </span>
      );
    } else if (property.type === 'array') {
      control = (
        <textarea
          {...commonProps}
          value={actionContext[field] ?? ''}
          onChange={event => updateField(field, event.target.value)}
          required={isRequired}
          placeholder={t('list_placeholder', 'Separe os valores por vírgula ou linha')}
          rows={3}
        />
      );
    } else {
      const inputType = ['integer', 'number'].includes(property.type) ? 'number' : 'text';
      control = (
        <input
          {...commonProps}
          type={inputType}
          step={property.type === 'integer' ? '1' : property.type === 'number' ? 'any' : undefined}
          min={property.minimum}
          max={property.maximum}
          minLength={property.minLength}
          maxLength={property.maxLength}
          pattern={property.pattern}
          value={actionContext[field] ?? ''}
          onChange={event => updateField(field, event.target.value)}
          required={isRequired}
        />
      );
    }

    return (
      <label key={field} className={`ai-copilot-field${property.type === 'boolean' ? ' ai-copilot-checkbox-field' : ''}`}>
        {label}
        {control}
        {property.description ? <small id={descriptionId}>{property.description}</small> : null}
        {showValidation ? (
          <small id={errorId} className="ai-copilot-error-text" role="alert">
            {validationMessage(validation)}
          </small>
        ) : null}
      </label>
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (
      !instruction.trim() || !selectedActionKey || isLoading || !isEligible || !isOnline ||
      hasInvalidActionField
    ) return;

    onCreateProposal(instruction.trim(), selectedActionKey, normalizedActionContext());
  };

  if (!selectedAction) return null;

  return (
    <section className="ai-copilot-action-step" aria-labelledby="ai-copilot-action-step-title">
      <header className="ai-copilot-step-header">
        <div>
          <p className="ai-copilot-kicker">{t('action_details', 'Detalhes da ação')}</p>
          <h3 id="ai-copilot-action-step-title" ref={headingRef} tabIndex="-1" data-ai-copilot-step-heading>
            {selectedAction.label || selectedActionKey.replace(/_/g, ' ')}
          </h3>
          <p>{selectedAction.description}</p>
        </div>
        <button type="button" className="ai-copilot-link" onClick={onChangeAction} disabled={isLoading}>
          {t('change_action', 'Trocar ação')}
        </button>
      </header>

      <form onSubmit={handleSubmit} className="ai-copilot-composer">
        <dl className="ai-copilot-action-summary">
          <div>
            <dt>{t('cost_estimated', 'Custo estimado')}</dt>
            <dd>{t('credits', '%{count} créditos', {
              count: selectedAction.estimated_credits_cost ?? selectedAction.credits_cost ?? 0
            })}</dd>
          </div>
          <div>
            <dt>{t('risk', 'Risco')}</dt>
            <dd>{translateEnum('risk', selectedAction.risk_level || 'low')}</dd>
          </div>
        </dl>

        {selectedAction.confirmation_mode === 'approval' ? (
          <p className="ai-copilot-notice" role="note">
            {t('approval_pending_help', 'A execução permanece bloqueada até uma pessoa autorizada decidir.')}
          </p>
        ) : null}

        {requiredActionFields.length > 0 ? (
          <fieldset className="ai-copilot-fieldset">
            <legend>{t('required_fields', 'Dados obrigatórios')}</legend>
            {requiredActionFields.map(renderField)}
          </fieldset>
        ) : null}

        {optionalActionFields.length > 0 ? (
          <details className="ai-copilot-optional-fields">
            <summary>{t('optional_fields', 'Mais detalhes (opcional)')}</summary>
            <div className="ai-copilot-optional-fields-body">
              {optionalActionFields.map(renderField)}
            </div>
          </details>
        ) : null}

        <label className="ai-copilot-field">
          <span>{t('instruction', 'O que o Copiloto deve considerar?')} *</span>
          <textarea
            value={instruction}
            onChange={event => setInstruction(event.target.value)}
            disabled={isLoading || !isEligible || !isOnline || !selectedActionKey}
            placeholder={t('instruction_placeholder', 'Descreva o objetivo e as restrições desta ação.')}
            rows={4}
            required
          />
        </label>

        <p className="ai-copilot-review-note" role="note">
          {t('review_safety', 'Nada será executado antes de você revisar a prévia.')}
        </p>
        <button
          type="submit"
          className="ai-copilot-primary"
          disabled={
            isLoading || !isEligible || !isOnline || !selectedActionKey ||
            instruction.trim() === '' || hasInvalidActionField
          }
        >
          {isLoading ? t('working', 'Preparando uma resposta segura...') : t('review_action', 'Revisar ação')}
        </button>
      </form>
    </section>
  );
};

AiChatMessenger.propTypes = {
  onCreateProposal: PropTypes.func.isRequired,
  onChangeAction: PropTypes.func,
  selectedActionKey: PropTypes.string,
  selectedAction: PropTypes.shape({
    action_key: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    risk_level: PropTypes.string,
    confirmation_mode: PropTypes.string,
    credits_cost: PropTypes.number,
    estimated_credits_cost: PropTypes.number,
    input_schema: PropTypes.shape({
      required: PropTypes.arrayOf(PropTypes.string),
      properties: PropTypes.object
    })
  }),
  initialOrderExceptionCaseId: PropTypes.number,
  isLoading: PropTypes.bool,
  isEligible: PropTypes.bool,
  isOnline: PropTypes.bool
};

export {
  hasSchemaValue,
  initialContextFor,
  schemaValueIsValid,
  validationForSchemaField,
  valueForSchema
};
export default AiChatMessenger;
